/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: <explanation> */

import { Location } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { format } from '@cobliteam/utils/date';
import qs from 'query-string';
import { useFleetCategories } from '../../../globalSidebarDef';
import { deviceStatusLabel, deviceTypeLabel, getCategory, vehicleLabel } from '../../../helpers';
import { useSearch } from '../../../hooks/useSearch';
import { type DeviceWithCategory, EnumCategory, type VehicleWithCategory } from '../../../types';
import CardHeader from '../../Cards/CardHeader';
import CardItem, { type CardItemProps } from '../../Cards/CardItem';
import CardMap from '../../Cards/CardMap';

const VehicleDetails = () => {
  const { t } = useTranslation('global-search');
  const { t: tMonitoring } = useTranslation('monitoring');
  const { data, selectedItem } = useSearch();
  const fleetCategories = useFleetCategories(t);
  if (!selectedItem) return null;
  const item = selectedItem as VehicleWithCategory;

  const driver = item?.driver || null;
  const allDevices = data?.filter((d) => d.category.id === EnumCategory.DEVICES) as DeviceWithCategory[];
  const device = allDevices?.find((d) => d.vehicle?.id === item?.id) || null;
  const hasTags = !!item.tags?.length;
  const limitSpeed = item.speedLimitInKmh ? `${item.speedLimitInKmh} Km/h` : '---';
  const fuelType = item.fuel?.type ? item.fuel.type : '---';
  const fuelPrice = item.fuel?.price ? `R$ ${item.fuel.price} / L` : '---';
  const fuelConsumption = item.fuel?.consumption ? `${item.fuel.consumption} Km/L` : '---';
  const size = item.size ? item.size : '---';
  const lastTripDate = device?.lastLocation?.timeInMillis ? format(device?.lastLocation?.timeInMillis, 'dd/MM/yyyy HH:mm') : '---';
  const lastTripParams = qs.stringify({
    vehicle_id: item.id,
    date: device?.lastLocation?.timeInMillis,
  });

  const deviceCategory = getCategory(EnumCategory.DEVICES, fleetCategories);
  const driverCategory = getCategory(EnumCategory.DRIVERS, fleetCategories);
  const groupsCategory = getCategory(EnumCategory.VEHICLE_GROUPS, fleetCategories);

  if (!(deviceCategory && driverCategory && groupsCategory)) return null;

  const cardLastTrip: CardItemProps = {
    disabled: !device?.lastLocation?.timeInMillis,
    title: t('vehicle_details.last_trip'),
    subtitle: lastTripDate,
    tooltip: t('vehicle_details.last_trip'),
    icon: 'route',
    color: 'accessory-sky',
    itemLink: `${Location.TRACKING}?${lastTripParams}`,
    pageLink: undefined,
  };

  const cardDevice: CardItemProps = {
    disabled: !device?.id,
    title: item?.cobliId ? `${item?.cobliId} - ${deviceTypeLabel(item?.deviceType)}` : t('vehicle_details.no_device_associated'),
    tooltip: device?.id ? t('vehicle_details.associated_device') : t('vehicle_details.no_device_associated'),
    subtitle: device?.generalStatus ? `Status: ${deviceStatusLabel(device.generalStatus, tMonitoring)}` : '---',
    icon: deviceCategory.icon,
    color: deviceCategory.color,
    itemLink: `${Location.DEVICES}?deviceAssociation=${device?.id ? 'associated' : 'not_associated'}&search=${device?.id}`,
    pageLink: `${Location.VEHICLES}?search=${item.licensePlate}`,
  };

  const cardDriver: CardItemProps = {
    disabled: !driver?.id,
    title: driver?.name || t('vehicle_details.no_driver_associated'),
    tooltip: driver?.id ? t('vehicle_details.associated_driver') : t('vehicle_details.no_driver_associated'),
    subtitle: `Nº Telefone: ${driver?.phoneNumbers?.map((phone) => phone).join(', ') || '---'}`,
    icon: driverCategory.icon,
    color: driverCategory.color,
    itemLink: `${Location.DRIVER_MANAGEMENT}?search=${driver?.id}`,
    pageLink: `${Location.VEHICLES}?search=${item.licensePlate}&tab=${driver?.id ? 'Associado' : 'Desassociado'}`,
  };

  const cardGroups: CardItemProps = {
    disabled: !item.groups?.length,
    title: item.groups[0]?.name || t('vehicle_group_details.no_group_associated'),
    tooltip: item.groups?.length ? t('vehicle_details.associated_group') : t('vehicle_details.no_group_associated'),
    icon: groupsCategory.icon,
    color: groupsCategory.color,
    itemLink: `${Location.VEHICLES_GROUPS}?search=${item.groups[0]?.id}`,
    pageLink: `${Location.VEHICLES_GROUPS}`,
  };

  const title = item.licensePlate || item.cobliId || '---';
  const subtitle = vehicleLabel(item);
  const tab = device?.id ? 'Associado' : 'Desassociado';

  return (
    <div className='relative flex h-[500px] w-full flex-col gap-4 overflow-hidden overflow-y-auto p-4'>
      <CardHeader
        icon={item.category.icon}
        color={item.category.color}
        title={title}
        subtitle={subtitle}
        linkTooltip={t('vehicle_details.go_to_vehicles_page')}
        link={`${Location.VEHICLES}?search=${item.licensePlate}&tab=${tab}`}
      />

      {/*Tags*/}
      {hasTags && (
        <div className='flex w-full flex-row flex-wrap items-center gap-2'>
          <Tooltip content='Tags'>
            <Icon name='tag' className='!size-3' />
          </Tooltip>
          {item.tags?.map((tag: any) => (
            <Tag key={tag.id} className='px-3' size='sm' label={tag.name} />
          ))}
        </div>
      )}

      {/*Details*/}
      <div className='grid grid-cols-12 gap-2'>
        <div className='col-span-5'>
          <div className='mb-1 flex items-center gap-2 font-medium text-xs'>
            <Icon name='fuel' className='!size-3' />
            {t('vehicle_details.fuel')}
          </div>
          <div className='ml-5 w-full'>
            <div className='text-xs'>
              {t('vehicle_details.type')} {fuelType}
            </div>
            <div className='text-xs'>
              {t('vehicle_details.price')} {fuelPrice}
            </div>
            <div className='text-xs'>
              {t('vehicle_details.consumption')} {fuelConsumption}
            </div>
          </div>
        </div>

        <div className='col-span-7'>
          <div className='mb-1 flex flex-1 items-center gap-2 font-medium text-xs'>
            <Icon name='receipt-text' className='!size-3' />
            {t('vehicle_details.details')}
          </div>
          <div className='ml-5 w-full'>
            <div className='text-xs'>
              {t('vehicle_details.size')} {size}
            </div>
            <div className='text-xs'>
              {t('vehicle_details.speed_limit')} {limitSpeed}
            </div>
          </div>
        </div>
      </div>

      {/*Cards*/}
      <div className='grid grid-cols-1 gap-2'>
        <CardItem {...cardLastTrip} />
        <CardItem {...cardDevice} />
        <CardItem {...cardDriver} />
        <CardItem {...cardGroups} />
      </div>

      {/*Map*/}
      <CardMap
        latitude={device?.lastLocation?.latitude}
        longitude={device?.lastLocation?.longitude}
        updatedAt={device?.lastLocation?.cobliIngestedAt}
        linkTo={`/monitoring?search=${item.licensePlate}&focus=true`}
      />
    </div>
  );
};

export default VehicleDetails;
